import { useAuthContext } from 'shared/components/AuthContext'
import PersonIcon from '@mui/icons-material/Person'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {
  AdminChip,
  Button,
  IconWrapper,
  Menu,
  MenuItem,
  UserIconWrapper,
} from './UserMenu.style'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isGlobalworth, paths } from 'config'
import { Consents, Logout as LogoutIcon, Padlock } from 'shared/assets'
import {
  AdminPanelSettingsOutlined,
  LockOutlined,
  PowerSettingsNew,
  VerifiedUserOutlined,
} from '@mui/icons-material'
import { useAuth, useEnterSystemAdminMode, useIsMobile } from 'shared/hooks'
import { useHistory } from 'react-router'
import { ConfirmDialog } from 'shared/components'
import ViewerModeMenuItem from '../ViewerModeMenuItem'
import SystemAdminModeDenialDialogs from '../SystemAdminModeDenialDialogs'
import { useCurrentUserCacheOnly } from 'shared/data'
import { useInitializeViewerMode } from './UserMenu.utils'
import { useIsSystemAdminMode, hasOAuthSignInProvider } from 'shared/utils'
import { SystemAdminModeDenial } from 'shared/types'

interface Option {
  label: string
  Icon?: React.ReactNode
  onClick: () => void
}

const UserMenu = () => {
  const { user } = useAuthContext()
  const { signOut } = useAuth()
  const history = useHistory()
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const { data: dataUser } = useCurrentUserCacheOnly()

  const viewerCanUseSystemAdminMode =
    dataUser?.viewer?.viewerCanUseSystemAdminMode

  useInitializeViewerMode(viewerCanUseSystemAdminMode)

  const isMobile = useIsMobile()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [systemAdminModeDenial, setSystemAdminModeDenial] =
    useState<SystemAdminModeDenial | null>(null)
  const isOAuthAccount = hasOAuthSignInProvider(user)

  const showSystemAdminModeDenial = (denial: SystemAdminModeDenial) => {
    handleClose()
    setSystemAdminModeDenial(denial)
  }

  const closeSystemAdminModeDenial = () => setSystemAdminModeDenial(null)

  // After reauthentication the mode may still be denied, e.g. without 2FA
  const enterSystemAdminMode = useEnterSystemAdminMode(
    showSystemAdminModeDenial
  )

  const goToSecurity = () => {
    closeSystemAdminModeDenial()
    history.push(paths.security)
  }

  const isSystemAdminMode = useIsSystemAdminMode()

  const options: Option[] = useMemo(() => {
    const menuOptions: Option[] = []

    if (!isOAuthAccount) {
      menuOptions.push({
        label: t('security'),
        Icon: isGlobalworth ? <Padlock /> : <LockOutlined fontSize="small" />,
        onClick: () => {
          history.push(paths.security)
          setAnchorEl(null)
        },
      })
    }

    menuOptions.push({
      label: t('nav.consents'),
      Icon: isGlobalworth ? (
        <Consents />
      ) : (
        <VerifiedUserOutlined fontSize="small" />
      ),
      onClick: () => {
        history.push(paths.consents)
        setAnchorEl(null)
      },
    })

    menuOptions.push({
      label: t('logout'),
      Icon: isGlobalworth ? (
        <LogoutIcon />
      ) : (
        <PowerSettingsNew fontSize="small" />
      ),
      onClick: () => {
        setAnchorEl(null)
        setIsModalVisible(true)
      },
    })

    return menuOptions
  }, [history, isOAuthAccount, t])

  return (
    <>
      <ConfirmDialog
        message={t('confirmLogout')}
        onCancel={() => setIsModalVisible(false)}
        open={isModalVisible}
        onConfirm={signOut}
      />
      <SystemAdminModeDenialDialogs
        denial={systemAdminModeDenial}
        onClose={closeSystemAdminModeDenial}
        onReauthenticated={enterSystemAdminMode}
        onEnableTwoFactor={goToSecurity}
      />
      <Menu
        id="user-menu"
        MenuListProps={{
          'aria-labelledby': 'user-menu-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isMobile && <MenuItem disabled>{user?.email}</MenuItem>}

        <ViewerModeMenuItem
          parentMenuOpen={open}
          dataUser={dataUser}
          onSystemAdminModeDenied={showSystemAdminModeDenial}
        />

        {options.map(({ label, Icon, onClick }) => (
          <MenuItem key={label} disableRipple onClick={onClick}>
            {Icon && <IconWrapper>{Icon}</IconWrapper>}
            {label}
          </MenuItem>
        ))}
      </Menu>
      <Button
        id="user-menu-button"
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        startIcon={
          <UserIconWrapper>
            {isSystemAdminMode ? (
              <AdminChip
                icon={<AdminPanelSettingsOutlined />}
                label={t('viewerMode.adminShort')}
              />
            ) : (
              <PersonIcon />
            )}
          </UserIconWrapper>
        }
        endIcon={<KeyboardArrowDownIcon />}
        fullWidth={false}
        onClick={handleClick}
        disableElevation
      >
        {!isMobile && user?.email}
      </Button>
    </>
  )
}

export default UserMenu
